<script lang="ts">
let activeAuthCompletion: Promise<void> | null = null;
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { isPendingLivenessCheckSentinelError } from '@peanuds/shared';
import { appConfig } from '../../providers/config';
import { redirectToAuth0Logout } from '../auth0-logout';
import {
  clearLoginBlockedState,
  getLoginBlockedStateFromCallback,
  saveLoginBlockedState,
} from '../login-blocked-state';
import { useAuthSessionStore } from '../store/auth-session.store';
import { isUserContextLoadError } from '../../store/user-context.errors';
import {
  consumePostLoginRedirectTarget,
  getStoredPostLoginRedirectTarget,
} from '../post-login-redirect-target';
import { resolvePostAuthNavigation } from '../post-auth-navigation';
import {
  clearPendingLivenessApprovedAt,
  getPendingLivenessApprovedAt,
  subscribePendingLivenessApproval,
} from '../../composables/pending-liveness-approval';
import {
  clearPendingLivenessCallbackState,
  getPendingLivenessCallbackStartedAt,
  hasPendingLivenessCallbackState,
  rememberAuthCallbackStateForPendingLiveness,
} from './pending-liveness-callback-state';

const SESSION_SYNC_TIMEOUT_MS = 15_000;
const LOGIN_FAILURE_MESSAGE = 'Authentication failed. Please try again.';

const router = useRouter();
const auth0 = useAuth0();
const authSessionStore = useAuthSessionStore();

const error = ref<string | null>(null);
let unsubscribePendingLivenessApproval: (() => void) | null = null;
let consumedPendingLivenessApprovedAt: number | null = null;

type CompleteAppSessionOptions = {
  ignoreInvalidStateError?: boolean;
};

const isInvalidStateError = (err: unknown) => {
  return err instanceof Error && err.message === 'Invalid state';
};

const clearPendingLivenessApprovalSubscription = () => {
  unsubscribePendingLivenessApproval?.();
  unsubscribePendingLivenessApproval = null;
};

const completeAppSession = async (options: CompleteAppSessionOptions = {}) => {
  const result = await authSessionStore.completeLoginCallback({
    timeoutMs: SESSION_SYNC_TIMEOUT_MS,
    auth0Client: auth0,
    ...(options.ignoreInvalidStateError ? { ignoreAuth0Error: isInvalidStateError } : {}),
  });

  console.debug('[AuthCallback] completeLoginCallback finished', {
    status: result.status,
    reason: result.reason,
  });

  if (result.status === 'redirecting') {
    console.debug('[AuthCallback] Waiting for redirect re-authorization to complete', {
      targetOrganizationId: result.targetOrganizationId,
    });
    return;
  }

  if (result.status === 'failed' && isUserContextLoadError(result.error)) {
    throw result.error;
  }

  if (result.status !== 'authenticated') {
    console.warn('[AuthCallback] Login completion did not authenticate', {
      status: result.status,
      reason: result.reason,
    });
    error.value = LOGIN_FAILURE_MESSAGE;
    return;
  }

  clearPendingLivenessCallbackState();

  const postAuthNavigation = resolvePostAuthNavigation({
    currentRoute: 'application',
    redirectTarget: getStoredPostLoginRedirectTarget(),
    state: {
      requiresBasicInfoCollection: authSessionStore.requiresBasicInfoCollection,
      requiresOrganizationCreation: authSessionStore.requiresOrganizationCreation,
      requiresOrganizationSelection: authSessionStore.requiresOrganizationSelection,
    },
  });

  if (postAuthNavigation.kind === 'route') {
    console.debug('[AuthCallback] Redirecting to post-auth setup', {
      routeName: postAuthNavigation.route.name,
      redirectTarget: postAuthNavigation.route.query.redirect,
    });

    await router.replace(postAuthNavigation.route);

    return;
  }

  const redirectTarget = consumePostLoginRedirectTarget();
  console.debug('[AuthCallback] Redirecting after successful login', {
    redirectTarget,
  });

  await router.replace(redirectTarget);
};

const retryAppSessionAfterPendingLivenessApproval = (
  callbackState: string | null,
  options: CompleteAppSessionOptions = {},
) => {
  if (unsubscribePendingLivenessApproval) {
    return;
  }

  const pendingLivenessStartedAt = getPendingLivenessCallbackStartedAt(callbackState);
  const pendingLivenessApprovedAt = getPendingLivenessApprovedAt();

  error.value = null;

  if (
    pendingLivenessStartedAt !== null &&
    pendingLivenessApprovedAt !== null &&
    pendingLivenessApprovedAt >= pendingLivenessStartedAt &&
    pendingLivenessApprovedAt !== consumedPendingLivenessApprovedAt
  ) {
    consumedPendingLivenessApprovedAt = pendingLivenessApprovedAt;
    clearPendingLivenessApprovedAt();
    void completeAppSession(options).catch((err: unknown) =>
      handleAuthCompleteError(err, callbackState, options),
    );
    return;
  }

  unsubscribePendingLivenessApproval = subscribePendingLivenessApproval(() => {
    clearPendingLivenessApprovalSubscription();

    void completeAppSession(options).catch((err: unknown) =>
      handleAuthCompleteError(err, callbackState, options),
    );
  });
};

const handleAuthCompleteError = (
  err: unknown,
  callbackState: string | null,
  options: CompleteAppSessionOptions = {},
) => {
  if (isPendingLivenessCheckSentinelError(err)) {
    retryAppSessionAfterPendingLivenessApproval(callbackState, options);
    return;
  }

  if (isUserContextLoadError(err)) {
    throw err;
  }

  console.warn('[AuthCallback] Login completion failed', err);
  error.value = err instanceof Error ? err.message : LOGIN_FAILURE_MESSAGE;
};

async function handleAuthComplete() {
  if (activeAuthCompletion !== null) {
    await activeAuthCompletion;
    return;
  }

  activeAuthCompletion = completeAuthRedirect();

  try {
    await activeAuthCompletion;
  } finally {
    activeAuthCompletion = null;
  }
}

async function completeAuthRedirect() {
  const callbackParams = new URLSearchParams(window.location.search);
  const callbackState = callbackParams.get('state');
  const auth0Error = callbackParams.get('error');
  const auth0ErrorDescription = callbackParams.get('error_description');
  const loginBlockedState = getLoginBlockedStateFromCallback(auth0Error, auth0ErrorDescription);

  clearLoginBlockedState();

  if (loginBlockedState) {
    saveLoginBlockedState(loginBlockedState);
    redirectToAuth0Logout({
      domain: appConfig.VITE_AUTH0_DOMAIN ?? '',
      clientId: appConfig.VITE_AUTH0_CLIENT_ID ?? '',
      returnTo: `${window.location.origin}/logout`,
    });
    return;
  }

  rememberAuthCallbackStateForPendingLiveness(callbackState);

  try {
    await auth0.handleRedirectCallback();
    await completeAppSession();
  } catch (err) {
    if (isInvalidStateError(err) && hasPendingLivenessCallbackState(callbackState)) {
      const resumeOptions = { ignoreInvalidStateError: true } satisfies CompleteAppSessionOptions;

      try {
        await completeAppSession(resumeOptions);
      } catch (resumeError) {
        handleAuthCompleteError(resumeError, callbackState, resumeOptions);
      }

      return;
    }

    handleAuthCompleteError(err, callbackState);
  }
}

onMounted(handleAuthComplete);
onUnmounted(clearPendingLivenessApprovalSubscription);
</script>

<template>
  <div class="auth-callback" data-testid="auth-callback">
    <template v-if="error">
      <i class="pi pi-exclamation-circle auth-callback__error-icon" />
      <p class="auth-callback__error" data-testid="auth-callback-error">{{ error }}</p>
    </template>
    <template v-else>
      <i class="pi pi-spin pi-spinner" />
      <p class="auth-callback__text">Completing login...</p>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.auth-callback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: $gap-md;
  font-size: $icon-xl;
  color: var(--p-primary-500);

  &__text {
    font-size: $text-md;
    color: var(--p-surface-600);
  }

  &__error-icon {
    color: var(--p-red-500);
  }

  &__error {
    font-size: $text-md;
    color: var(--p-red-600);
    text-align: center;
    max-width: 400px;
  }
}
</style>
